<template>
  <div class="home-page">
    <section class="home-hero">
      <div class="home-hero__fallback" role="img" aria-label="玉雕作品"></div>

      <div class="home-hero__shade"></div>
      <div class="site-shell home-hero__content">
        <p class="eyebrow">中国玉雕文化档案平台</p>
        <h1>玉雕匠人</h1>
        <p class="home-hero__lead">以作品见技艺，以证书存信任，记录当代玉雕匠人的手艺与时间。</p>
        <div class="home-hero__actions">
          <router-link to="/gallery" class="jade-button jade-button--light">鉴赏作品 <i class="el-icon-arrow-right"></i></router-link>
          <router-link to="/masters" class="jade-button jade-button--ghost">认识匠人</router-link>
        </div>
      </div>
    </section>

    <section class="verification-band">
      <div class="site-shell verification-band__inner">
        <div class="verification-band__copy">
          <div>
            <p class="eyebrow">CERTIFICATE VERIFICATION</p>
            <h2>查询作品证书</h2>
            <p>输入证书编号与验证码，核验作品档案。</p>
          </div>
        </div>
        <el-form ref="queryForm" :model="queryForm" :rules="queryRules" class="quick-query">
          <el-form-item prop="certNo">
            <el-input v-model="queryForm.certNo" placeholder="证书编号" prefix-icon="el-icon-document"></el-input>
          </el-form-item>
          <el-form-item prop="captcha">
            <div class="captcha-field">
              <el-input v-model="queryForm.captcha" placeholder="验证码" prefix-icon="el-icon-key" @keyup.enter.native="submitQuery"></el-input>
              <button type="button" class="captcha-code" title="点击刷新验证码" @click="refreshCaptcha">{{ captchaText }}</button>
            </div>
          </el-form-item>
          <el-form-item class="quick-query__actions">
            <el-button type="primary" @click="submitQuery">立即查询</el-button>
            <el-button title="重置" icon="el-icon-refresh" @click="resetQuery"></el-button>
          </el-form-item>
        </el-form>
      </div>
    </section>

    <section class="content-section content-section--works">
      <div class="site-shell">
        <header class="editorial-heading">
          <div>
            <p class="eyebrow">CURATED WORKS</p>
            <h2>玉作新赏</h2>
          </div>
          <p>从材质、构思到刀法，近距离观看每一件作品的细节。</p>
          <router-link to="/gallery" class="text-link">查看全部 <i class="el-icon-arrow-right"></i></router-link>
        </header>

        <div v-if="collectionList.length" class="work-preview-grid">
          <article v-for="item in collectionList.slice(0, 8)" :key="item.id" class="work-preview" @click="$router.push('/gallery')">
            <div class="work-preview__image">
              <img v-if="item.artworkImage" :src="getImageUrl(item.artworkImage)" :alt="item.name" @error="handleImageError">
              <div v-else class="image-placeholder"><i class="el-icon-picture-outline"></i></div>
              <span v-if="item.isRare" class="work-preview__badge">珍藏</span>
            </div>
            <div class="work-preview__info">
              <h3>{{ item.name }}</h3>
              <p>{{ item.description || '玉雕作品' }}</p>
            </div>
          </article>
        </div>
        <el-empty v-else description="暂无作品数据"></el-empty>
      </div>
    </section>

    <section class="content-section content-section--artisans">
      <div class="site-shell">
        <header class="editorial-heading editorial-heading--inverse">
          <div>
            <p class="eyebrow">ARTISAN DIRECTORY</p>
            <h2>见人 · 见艺</h2>
          </div>
          <p>认识长期专注于玉石雕刻、持续锤炼手艺的当代匠人。</p>
          <router-link to="/masters" class="text-link">匠人名录 <i class="el-icon-arrow-right"></i></router-link>
        </header>

        <div class="artisan-preview-grid">
          <article v-for="item in masterList.slice(0, 6)" :key="item.id" class="artisan-preview" @click="goToMasterDetail(item.id)">
            <div class="artisan-preview__portrait">
              <img v-if="item.avatar" :src="getFullImageUrl(item.avatar)" :alt="item.masterName" @error="handleImageError">
              <div v-else class="image-placeholder"><i class="el-icon-user"></i></div>
            </div>
            <div class="artisan-preview__info">
              <h3>{{ item.masterName }}</h3>
              <p>{{ item.craft || item.title || '玉雕技艺' }}</p>
              <span>查看档案 <i class="el-icon-arrow-right"></i></span>
            </div>
          </article>
        </div>
      </div>
    </section>

    <section class="content-section content-section--news">
      <div class="site-shell">
        <header class="editorial-heading">
          <div>
            <p class="eyebrow">JADE JOURNAL</p>
            <h2>行业见闻</h2>
          </div>
          <p>关注玉雕创作、文化传承与行业发展的最新动态。</p>
          <router-link to="/news" class="text-link">全部资讯 <i class="el-icon-arrow-right"></i></router-link>
        </header>

        <div class="home-news-list">
          <article v-for="item in newsList.slice(0, 5)" :key="item.id" class="home-news-item" @click="goToNewsDetail(item.id)">
            <div class="home-news-item__image">
              <img v-if="item.coverImage" :src="getFullImageUrl(item.coverImage)" :alt="item.title" @error="handleImageError">
              <div v-else class="image-placeholder"><i class="el-icon-reading"></i></div>
            </div>
            <div class="home-news-item__copy">
              <time>{{ formatDate(item.publishTime) }}</time>
              <h3>{{ item.title }}</h3>
              <p>{{ getSummary(item.content) }}</p>
            </div>
            <i class="el-icon-arrow-right home-news-item__arrow"></i>
          </article>
        </div>
      </div>
    </section>
  </div>
</template>

<script>
import { getCollectionList } from '@/api/collection'
import { getMastersList } from '@/api/masters'
import { getNewsList } from '@/api/news'

export default {
  name: 'HomePage',
  data() {
    return {
      collectionList: [],
      masterList: [],
      newsList: [],
      captchaText: '',
      queryForm: { certNo: '', captcha: '' },
      queryRules: {
        certNo: [{ required: true, message: '请输入证书编号', trigger: 'blur' }],
        captcha: [{ required: true, message: '请输入验证码', trigger: 'blur' }]
      }
    }
  },
  created() {
    this.refreshCaptcha()
    this.fetchCollections()
    this.fetchMasters()
    this.fetchNews()
  },
  methods: {
    async fetchCollections() {
      try {
        const response = await getCollectionList({ pageNum: 1, pageSize: 1000 })
        if (response.code === 200) this.collectionList = this.shuffleItems(response.rows || []).slice(0, 8)
      } catch (error) {
        console.error('获取作品数据出错:', error)
      }
    },
    async fetchMasters() {
      try {
        const response = await getMastersList({ pageNum: 1, pageSize: 1000 })
        if (response.code === 200) {
          this.masterList = this.shuffleItems(response.rows || []).slice(0, 6)
        }
      } catch (error) {
        console.error('获取匠人数据出错:', error)
      }
    },
    async fetchNews() {
      try {
        const response = await getNewsList({ pageNum: 1, pageSize: 1000 })
        if (response.code === 200) this.newsList = this.shuffleItems(response.rows || []).slice(0, 5)
      } catch (error) {
        console.error('获取资讯数据出错:', error)
      }
    },
    refreshCaptcha() {
      this.captchaText = Math.random().toString(36).substr(2, 4).toUpperCase()
    },
    submitQuery() {
      this.$refs.queryForm.validate(valid => {
        if (!valid) return
        if (this.queryForm.captcha.toUpperCase() !== this.captchaText) {
          this.$message.error('验证码错误！')
          this.refreshCaptcha()
          return
        }
        this.$router.push('/certificate?id=' + encodeURIComponent(this.queryForm.certNo.trim()))
      })
    },
    resetQuery() {
      this.$refs.queryForm.resetFields()
      this.refreshCaptcha()
    },
    shuffleItems(items) {
      const shuffled = items.slice()
      for (let index = shuffled.length - 1; index > 0; index -= 1) {
        const target = Math.floor(Math.random() * (index + 1))
        ;[shuffled[index], shuffled[target]] = [shuffled[target], shuffled[index]]
      }
      return shuffled
    },
    getImageUrl(path) {
      if (!path) return ''
      return path.startsWith('http://') || path.startsWith('https://') ? path : `${process.env.VUE_APP_BASE_API}${path}`
    },
    getFullImageUrl(path) {
      return this.getImageUrl(path)
    },
    handleImageError(event) {
      if (event && event.target) event.target.style.display = 'none'
    },
    getSummary(content) {
      if (!content) return '暂无内容'
      const text = content.replace(/<[^>]+>/g, '')
      return text.length > 72 ? text.substring(0, 72) + '...' : text
    },
    formatDate(dateStr) {
      return dateStr ? new Date(dateStr).toLocaleDateString() : ''
    },
    goToMasterDetail(id) {
      this.$router.push(`/masters/${id}`)
    },
    goToNewsDetail(id) {
      this.$router.push(`/news/${id}`)
    }
  }
}
</script>
